/**
 * Static palettes, keys and registries for the Validation Rules page.
 *
 * Everything here is pure data - no React, no side effects - so it can be
 * imported by both the page component and pure helper functions in
 * `utils.ts`. Keep runtime code out of this file.
 */
import {
  Award,
  ClipboardList,
  Globe,
  Landmark,
  Lightbulb,
  Link2,
  SlidersHorizontal,
} from "lucide-react";
import type {
  IntelligenceLayerId,
  IntelligenceLayerMeta,
  RadioTone,
} from "./types";

// ─── Local storage keys ─────────────────────────────────────────────────────

export const LS_ORG_KEY = "validation-rules:org";
export const LS_STATUS_KEY = "validation-rules:status";
export const LS_UNPUBLISHED_KEY = "validation-rules:unpublished-packs";
export const LS_LAYER_KEY = "validation-rules:layer";

// ─── API error code → user message ──────────────────────────────────────────

export const ERROR_CODE_MESSAGES: Record<string, string> = {
  "GOV-0030-E": "Validation rule not found.",
  "GOV-0031-E": "An organization can't be changed after a rule is created.",
};

// ─── Semantic Intelligence Layers ──────────────────────────────────────────

export const INTELLIGENCE_LAYERS: IntelligenceLayerMeta[] = [
  {
    id: "DOMAIN",
    order: "01",
    label: "Domain Intelligence",
    icon: Globe,
    tone: {
      ring: "ring-indigo-300 dark:ring-indigo-500/40",
      iconBg: "bg-indigo-50 dark:bg-indigo-500/10",
      iconText: "text-indigo-600 dark:text-indigo-400",
      activeBadgeText: "text-indigo-600 dark:text-indigo-400",
      dot: "bg-indigo-500",
      selectedBg:
        "bg-gradient-to-b from-indigo-50/70 to-transparent dark:from-indigo-500/[0.06]",
    },
  },
  {
    id: "REQUIREMENT",
    order: "02",
    label: "Requirement Intelligence",
    icon: ClipboardList,
    tone: {
      ring: "ring-sky-300 dark:ring-sky-500/40",
      iconBg: "bg-sky-50 dark:bg-sky-500/10",
      iconText: "text-sky-600 dark:text-sky-400",
      activeBadgeText: "text-sky-600 dark:text-sky-400",
      dot: "bg-sky-500",
      selectedBg:
        "bg-gradient-to-b from-sky-50/70 to-transparent dark:from-sky-500/[0.06]",
    },
  },
  {
    id: "CONCEPT",
    order: "03",
    label: "Concept Intelligence",
    icon: Lightbulb,
    tone: {
      ring: "ring-teal-300 dark:ring-teal-500/40",
      iconBg: "bg-teal-50 dark:bg-teal-500/10",
      iconText: "text-teal-600 dark:text-teal-400",
      activeBadgeText: "text-teal-600 dark:text-teal-400",
      dot: "bg-teal-500",
      selectedBg:
        "bg-gradient-to-b from-teal-50/70 to-transparent dark:from-teal-500/[0.06]",
    },
  },
  {
    id: "RELATIONSHIP",
    order: "04",
    label: "Relationship Intelligence",
    icon: Link2,
    tone: {
      ring: "ring-emerald-300 dark:ring-emerald-500/40",
      iconBg: "bg-emerald-50 dark:bg-emerald-500/10",
      iconText: "text-emerald-600 dark:text-emerald-400",
      activeBadgeText: "text-emerald-600 dark:text-emerald-400",
      dot: "bg-emerald-500",
      selectedBg:
        "bg-gradient-to-b from-emerald-50/70 to-transparent dark:from-emerald-500/[0.06]",
    },
  },
  {
    id: "CONSISTENCY",
    order: "05",
    label: "Consistency Intelligence",
    icon: SlidersHorizontal,
    tone: {
      ring: "ring-amber-300 dark:ring-amber-500/40",
      iconBg: "bg-amber-50 dark:bg-amber-500/10",
      iconText: "text-amber-600 dark:text-amber-400",
      activeBadgeText: "text-amber-600 dark:text-amber-400",
      dot: "bg-amber-500",
      selectedBg:
        "bg-gradient-to-b from-amber-50/70 to-transparent dark:from-amber-500/[0.06]",
    },
  },
  {
    id: "KNOWLEDGE",
    order: "06",
    label: "Knowledge Quality",
    icon: Award,
    tone: {
      ring: "ring-rose-300 dark:ring-rose-500/40",
      iconBg: "bg-rose-50 dark:bg-rose-500/10",
      iconText: "text-rose-600 dark:text-rose-400",
      activeBadgeText: "text-rose-600 dark:text-rose-400",
      dot: "bg-rose-500",
      selectedBg:
        "bg-gradient-to-b from-rose-50/70 to-transparent dark:from-rose-500/[0.06]",
    },
  },
  {
    id: "GOVERNANCE",
    order: "07",
    label: "Governance Intelligence",
    icon: Landmark,
    tone: {
      ring: "ring-purple-300 dark:ring-purple-500/40",
      iconBg: "bg-purple-50 dark:bg-purple-500/10",
      iconText: "text-purple-600 dark:text-purple-400",
      activeBadgeText: "text-purple-600 dark:text-purple-400",
      dot: "bg-purple-500",
      selectedBg:
        "bg-gradient-to-b from-purple-50/70 to-transparent dark:from-purple-500/[0.06]",
    },
  },
];

export const LAYER_META_BY_ID: Record<
  IntelligenceLayerId,
  IntelligenceLayerMeta
> = INTELLIGENCE_LAYERS.reduce(
  (acc, l) => {
    acc[l.id] = l;
    return acc;
  },
  {} as Record<IntelligenceLayerId, IntelligenceLayerMeta>,
);

// ─── Badge geometry ─────────────────────────────────────────────────────────

/** Consistent pill geometry used by every badge on the page.
 *  `min-w-[88px]` keeps every badge in a column at the same width regardless
 *  of label length (e.g. "Mandatory" vs "Standard"). */
export const BADGE_BASE =
  "inline-flex items-center justify-center min-w-[88px] text-[10px] font-bold tracking-wide px-2.5 py-1 rounded-full border whitespace-nowrap leading-none";

/** Same pill geometry as `BADGE_BASE` but ring-based (no border) so the
 *  category badge matches the Content Registry document-type palette. */
export const CATEGORY_BADGE_BASE =
  "inline-flex items-center justify-center min-w-[88px] text-[10px] font-bold capitalize tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap leading-none";

/** Category colours mirror the Content Registry document-type palette
 *  (bg + text + ring), so the same hues are reused across both pages. */
export const CATEGORY_COLORS: Record<string, string> = {
  metadata:
    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200/60 dark:ring-blue-500/20",
  structural:
    "bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 ring-1 ring-fuchsia-200/60 dark:ring-fuchsia-500/20",
  semantic:
    "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-1 ring-violet-200/60 dark:ring-violet-500/20",
  content:
    "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 ring-1 ring-orange-200/60 dark:ring-orange-500/20",
};

export const DEFAULT_CATEGORY_COLOR =
  "bg-slate-100 dark:bg-white/[0.05] text-slate-700 dark:text-white/60 ring-1 ring-slate-200/60 dark:ring-white/[0.08]";

// ─── Radio card palette ─────────────────────────────────────────────────────

export const RADIO_TONE: Record<
  RadioTone,
  { active: string; iconActive: string; iconIdle: string }
> = {
  red: {
    active:
      "border-red-500 bg-red-50 dark:bg-red-500/10 dark:border-red-500/60",
    iconActive: "text-red-600 dark:text-red-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  amber: {
    active:
      "border-amber-500 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/60",
    iconActive: "text-amber-600 dark:text-amber-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  emerald: {
    active:
      "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/60",
    iconActive: "text-emerald-600 dark:text-emerald-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  slate: {
    active:
      "border-slate-400 bg-slate-50 dark:bg-white/[0.06] dark:border-white/30",
    iconActive: "text-slate-600 dark:text-white/80",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  indigo: {
    active:
      "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 dark:border-indigo-500/60",
    iconActive: "text-indigo-600 dark:text-indigo-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
};
